import { createForm, type RenderParams } from '@seek/forms-ui';
import { mapDropdownItemToOption } from '@seek/profile-edit-ui';
import { rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Inline,
  Stack,
  TextField,
  Dropdown,
  Textarea,
  Button,
  Notice,
  Text,
  useToast,
} from 'braid-design-system';
import React, { useEffect, useState } from 'react';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { useMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import { REPORT_PROFILE_FORM } from 'src/reducers/forms';
import * as Schema from 'src/types/graphql';

import translations from './.vocab';

const mapFormStateToProps = ({
  form: { dirty: isFormDirty },
}: {
  form: {
    dirty: boolean;
  };
}) => ({
  isFormDirty,
});

type MappedProps = ReturnType<typeof mapFormStateToProps>;

interface FormValues {
  email: string;
  reason: string;
  comment: string;
}

type FormProps = RenderParams<FormValues> & MappedProps;

const { FormProvider, useField } = createForm<FormValues, MappedProps>(
  REPORT_PROFILE_FORM,
  {
    formType: 'braid',
  },
);

const initialValues: FormValues = {
  email: '',
  reason: '',
  comment: '',
};

const Form = ({
  handleSubmit,
  isFormDirty,
  onFormDirtyChange,
}: FormProps & DirtyFormProps) => {
  const { t } = useTranslations(translations);
  const { zone } = useAppConfig();
  const profileSlug = useTypedSelector(
    (store) => store.router.shareableProfileSlug,
  );
  const [submitted, setSubmitted] = useState(false);

  useOnFormDirtyChange({ onFormDirtyChange, dirty: isFormDirty });

  const reasonOptions = [
    {
      value: 'This profile contains inappropriate content',
      label: t('This profile contains inappropriate content'),
    },
    {
      value: 'This profile belongs to a company, not an individual',
      label: t('This profile belongs to a company, not an individual'),
    },
  ];

  const emailField = useField({
    id: 'email',
    ...rules.reporterEmail({ requiredLabel: t('your email') }),
  });
  const reasonField = useField({
    id: 'reason',
    ...rules.reportReason({ requiredLabel: t('a reason') }),
  });
  const commentField = useField({
    id: 'comment',
    ...rules.reportComments({ requiredLabel: t('additional comments') }),
  });

  const [reportProfileInput, { data: mutationData, loading, error }] =
    useMutation(Schema.ReportProfileDocument);

  const showToast = useToast();

  useEffect(() => {
    if (mutationData && 'ref' in mutationData.reportProfile) {
      setSubmitted(true);
      onFormDirtyChange(false);
    } else if (
      (mutationData && 'error' in mutationData.reportProfile) ||
      error
    ) {
      showToast({
        key: 'report-error',
        tone: 'critical',
        message: t('Your report could not be sent'),
        description: t('Please try again later'),
      });
    }
  }, [mutationData, showToast, error, t, onFormDirtyChange]);

  const onSubmit = (formValues: FormValues) => {
    reportProfileInput({
      variables: {
        report: {
          ...formValues,
          profileSlug,
          zone,
        },
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {!submitted ? (
        <>
          <Stack space="gutter">
            <TextField
              id="email"
              label={t('Your email address')}
              placeholder={t('Enter email address')}
              data={{ automation: 'reporter-email' }}
              {...emailField}
            />
            <Dropdown
              id="reason"
              label={t('Reason for reporting profile')}
              placeholder={t('Please select')}
              data={{ automation: 'reason-dropdown' }}
              {...reasonField}
            >
              {reasonOptions.map(mapDropdownItemToOption)}
            </Dropdown>
            <Textarea
              id="comment"
              label={t('Additional comments')}
              characterLimit={rules.reportComments().validatorProps.max}
              placeholder={t(
                'Provide any additional information that may help us recognise the issue.',
              )}
              data={{ automation: 'report-comment' }}
              {...commentField}
              value={commentField.value || ''}
              highlightRanges={commentField.highlightRanges || undefined}
            />
            <Inline space="small" collapseBelow="desktop">
              <Button
                type="submit"
                tone="formAccent"
                data={{ automation: 'report-profile-submit-button' }}
                loading={loading}
              >
                {t('Submit')}
              </Button>
            </Inline>
          </Stack>
        </>
      ) : (
        <Notice tone="positive">
          <Text>
            {t(
              'Thank you. Your report has been forwarded to our support team.',
            )}
          </Text>
        </Notice>
      )}
    </form>
  );
};

export const ReportProfileForm = (dirtyFormProps: DirtyFormProps) => (
  <FormProvider
    mapFormStateToProps={mapFormStateToProps}
    initialValues={initialValues}
  >
    {(formProps) => <Form {...dirtyFormProps} {...formProps} />}
  </FormProvider>
);
